import { useEffect, useState } from "react";
import { Pressable, View } from "react-native";

import {
  AppText,
  Button,
  ButtonText,
  Row,
  SearchableSelect,
  SegmentedControl,
  TextField,
} from "~/components/ui";
import type { Tables } from "~/api/tables";

import { makeStylesheet } from "./settings.styles";
import { useThemedStyles } from "~/styles/theme";
export type ProjectStatus = Tables<"project_statuses">;
export type ProjectState = ProjectStatus["state"];
export type RatePreset = Tables<"user_rates">;

export const projectStateOptions: { label: string; value: ProjectState }[] = [
  { label: "Active", value: "active" },
  { label: "Paused", value: "paused" },
  { label: "Done", value: "completed" },
  { label: "Archived", value: "archived" },
];

export const projectStatusColours = [
  "#0F766E",
  "#2563EB",
  "#7C3AED",
  "#D97706",
  "#DC2626",
  "#475569",
] as const;

export function StatusColourPicker({
  value,
  onChange,
  disabled = false,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const stylesheet = useThemedStyles(makeStylesheet);
  return (
    <View
      accessibilityLabel="Project status colour"
      accessibilityRole="radiogroup"
      style={stylesheet.statusColours}
    >
      {projectStatusColours.map((colour) => {
        const selected = colour === value;
        return (
          <Pressable
            key={colour}
            accessibilityRole="radio"
            accessibilityLabel={`Use colour ${colour}`}
            accessibilityState={{ checked: selected, disabled }}
            disabled={disabled}
            onPress={() => onChange(colour)}
            style={[
              stylesheet.statusColourButton,
              selected ? stylesheet.statusColourButtonSelected : null,
            ]}
          >
            <View
              style={[
                stylesheet.statusColourSwatch,
                { backgroundColor: colour },
              ]}
            />
          </Pressable>
        );
      })}
    </View>
  );
}

export function ProjectStatusEditor({
  status,
  disabled,
  onSave,
  onRemove,
}: {
  status: ProjectStatus;
  disabled: boolean;
  onSave: (input: {
    id: string;
    name: string;
    color: string;
    state: ProjectState;
  }) => Promise<unknown>;
  onRemove: (status: ProjectStatus) => void;
}) {
  const stylesheet = useThemedStyles(makeStylesheet);
  const [name, setName] = useState(status.name);
  const [colour, setColour] = useState(status.color);
  const [state, setState] = useState<ProjectState>(status.state);

  useEffect(() => {
    setName(status.name);
    setColour(status.color);
    setState(status.state);
  }, [status]);

  const changed =
    name.trim() !== status.name ||
    colour !== status.color ||
    state !== status.state;

  return (
    <View style={stylesheet.statusEditor}>
      <View style={stylesheet.statusEditorMain}>
        <View style={stylesheet.statusNameField}>
          <AppText size="xs" weight="semibold">
            Status name
          </AppText>
          <TextField
            value={name}
            editable={!disabled}
            muted={disabled}
            onChangeText={setName}
          />
        </View>
        <View style={stylesheet.statusStateField}>
          <AppText size="xs" weight="semibold">
            Behaviour
          </AppText>
          <SegmentedControl
            accessibilityLabel={`${status.name} behaviour`}
            options={projectStateOptions}
            value={state}
            onChange={setState}
            disabled={disabled}
          />
        </View>
        <View style={stylesheet.statusColourField}>
          <AppText size="xs" weight="semibold">
            Colour
          </AppText>
          <StatusColourPicker
            value={colour}
            disabled={disabled}
            onChange={setColour}
          />
        </View>
      </View>
      <Row>
        <Button
          compact
          variant="outline"
          disabled={disabled || !changed || !name.trim()}
          onPress={async () => {
            try {
              await onSave({ id: status.id, name, color: colour, state });
            } catch {
              // Mutation errors are shown by the parent settings panel.
            }
          }}
        >
          <ButtonText>Save</ButtonText>
        </Button>
        <Button
          compact
          variant="outline"
          disabled={disabled}
          onPress={() => onRemove(status)}
        >
          <ButtonText>Remove</ButtonText>
        </Button>
      </Row>
    </View>
  );
}

export function RatePresetEditor({
  rate,
  clients,
  projects,
  disabled,
  onSave,
  onRemove,
}: {
  rate: RatePreset;
  clients: { id: string; name: string }[];
  projects: { client_id: string | null; id: string; name: string }[];
  disabled: boolean;
  onSave: (input: {
    id: string;
    values: Record<string, string>;
  }) => Promise<unknown>;
  onRemove: (rate: RatePreset) => void;
}) {
  const stylesheet = useThemedStyles(makeStylesheet);
  const [name, setName] = useState(rate.name);
  const [description, setDescription] = useState(rate.description ?? "");
  const [hourlyRate, setHourlyRate] = useState(
    String(rate.hourly_rate_cents / 100),
  );
  const [effectiveFrom, setEffectiveFrom] = useState(rate.effective_from);
  const [clientId, setClientId] = useState(rate.client_id ?? "");
  const [projectId, setProjectId] = useState(rate.project_id ?? "");

  useEffect(() => {
    setName(rate.name);
    setDescription(rate.description ?? "");
    setHourlyRate(String(rate.hourly_rate_cents / 100));
    setEffectiveFrom(rate.effective_from);
    setClientId(rate.client_id ?? "");
    setProjectId(rate.project_id ?? "");
  }, [rate]);

  const clientOptions = [
    { label: "All clients", value: "" },
    ...clients.map((client) => ({ label: client.name, value: client.id })),
  ];
  const projectOptions = [
    { label: "All projects", value: "" },
    ...projects
      .filter((project) => !clientId || project.client_id === clientId)
      .map((project) => ({ label: project.name, value: project.id })),
  ];
  const changed =
    name.trim() !== rate.name ||
    description.trim() !== (rate.description ?? "") ||
    hourlyRate !== String(rate.hourly_rate_cents / 100) ||
    effectiveFrom !== rate.effective_from ||
    clientId !== (rate.client_id ?? "") ||
    projectId !== (rate.project_id ?? "");

  function selectProject(value: string) {
    setProjectId(value);
    const project = projects.find((candidate) => candidate.id === value);
    if (project?.client_id) setClientId(project.client_id);
  }

  return (
    <View style={stylesheet.rateEditor}>
      <View style={stylesheet.rateEditorGrid}>
        <View style={stylesheet.rateNameField}>
          <AppText size="xs" weight="semibold">
            Name
          </AppText>
          <TextField
            value={name}
            editable={!disabled}
            muted={disabled}
            onChangeText={setName}
          />
        </View>
        <View style={stylesheet.rateField}>
          <AppText size="xs" weight="semibold">
            Hourly rate
          </AppText>
          <TextField
            value={hourlyRate}
            editable={!disabled}
            muted={disabled}
            keyboardType="numeric"
            onChangeText={setHourlyRate}
          />
        </View>
        <View style={stylesheet.rateField}>
          <AppText size="xs" weight="semibold">
            Effective from
          </AppText>
          <TextField
            value={effectiveFrom}
            editable={!disabled}
            muted={disabled}
            placeholder="2026-07-01"
            onChangeText={setEffectiveFrom}
          />
        </View>
        <View style={stylesheet.rateScopeField}>
          <AppText size="xs" weight="semibold">
            Client scope
          </AppText>
          <SearchableSelect
            title="Choose rate client"
            value={clientId}
            options={clientOptions}
            disabled={disabled}
            onValueChange={(value) => {
              setClientId(value);
              setProjectId("");
            }}
          />
        </View>
        <View style={stylesheet.rateScopeField}>
          <AppText size="xs" weight="semibold">
            Project scope
          </AppText>
          <SearchableSelect
            title="Choose rate project"
            value={projectId}
            options={projectOptions}
            disabled={disabled}
            onValueChange={selectProject}
          />
        </View>
        <View style={stylesheet.rateDescriptionField}>
          <AppText size="xs" weight="semibold">
            Description
          </AppText>
          <TextField
            value={description}
            editable={!disabled}
            muted={disabled}
            placeholder="When this rate should apply"
            onChangeText={setDescription}
          />
        </View>
      </View>
      <Row>
        <Button
          compact
          variant="outline"
          disabled={disabled || !changed || !name.trim() || !hourlyRate.trim()}
          onPress={async () => {
            try {
              await onSave({
                id: rate.id,
                values: {
                  clientId,
                  description,
                  effectiveFrom,
                  hourlyRate,
                  name,
                  projectId,
                },
              });
            } catch {
              // Mutation errors are shown by the parent settings panel.
            }
          }}
        >
          <ButtonText>Save</ButtonText>
        </Button>
        <Button
          compact
          variant="outline"
          disabled={disabled}
          onPress={() => onRemove(rate)}
        >
          <ButtonText>Remove</ButtonText>
        </Button>
      </Row>
    </View>
  );
}
